import { BrutusCaseRunCreateModel, BrutusCaseRunCreateSampleModel, BrutusRelationModel, BrutusTestType, KitPopulationClient, LabClient, LabReadModel, PopulationSearchResultModel } from '@app/api2/api';
import SemanticField from '@app/components/SemanticField';
import { Formik } from 'formik';
import * as React from 'react';
import * as yup from "yup";
import { Button, Checkbox, CheckboxProps, Container, Dropdown, Form, Grid, GridColumn, GridRow, Modal, Segment, Table, TableBody, TableCell } from 'semantic-ui-react';
import { customAlphabet } from 'nanoid';
import { PedigreeEditor } from './PedigreeEditor';
import { BPButton } from '@/components/bp/bpbutton';
import { BPContainer } from '@/components/bp/bpcontainer';

interface IInformationBrutus2Props {
    model: BrutusCaseRunCreateModel;
    onComplete: (values: InformationBrutusValuesState) => void;
    router: any;
}

export type InformationBrutusValuesState = Pick<BrutusCaseRunCreateModel, 'description' | 'populationId' | 'references' | 'relations' | 'h1' | 'h2' | "brutusTestType">;
// type TypedRelations = keyof RelationsResources
type TypedRelations = 'parent' | 'child';
type BrutusCaseRunCreateSampleModelExt = Omit<BrutusCaseRunCreateSampleModel, 'id'> & { id: number | string }
// type BrutusCalcType = 'kinship' | 'freeform'

export const InformationBrutus2: React.FunctionComponent<IInformationBrutus2Props> = (props) => {
    const t = window.translation;
    const [labs, setLabs] = React.useState<LabReadModel[]>();
    const [populations, setPopulations] = React.useState<PopulationSearchResultModel[]>();
    const client = new KitPopulationClient();
    const labclient = new LabClient();
    const validationSchema: yup.SchemaOf<InformationBrutusValuesState> = yup.object().shape({
        description: yup.string().label(window.translation.caseRun.runDescription).nullable().notRequired(),
        populationId: yup.number().label(window.translation.population.name).required(),
        references: yup.array().label(window.translation.common.referencePlural).optional().nullable().notRequired(),
        relations: yup.array().label(window.translation.common.active).optional().nullable().notRequired(),
    }) as any;

    const [h1References, setH1References] = React.useState<BrutusCaseRunCreateSampleModel[]>([...(props.model.h1?.references ?? props.model.references ?? [])]);
    const [h1Relations, setH1Relations] = React.useState<BrutusRelationModel[]>([...(props.model.h1?.relations ?? props.model.relations ?? [])]);

    const [h2References, setH2References] = React.useState<BrutusCaseRunCreateSampleModel[]>([...(props.model.h2?.references ?? [])]);
    const [h2Relations, setH2Relations] = React.useState<BrutusRelationModel[]>([...(props.model.h2?.relations ?? [])]);

    const [calcType, setCalcType] = React.useState<BrutusTestType>(props.model.brutusTestType);

    const model = props.model;

    React.useEffect(() => {
        async function fetch() {
            let pageData = await labclient.getAll();
            setLabs(pageData.labs)

            let pageData2 = await client.pageData(1000, 1, '', 'asc', '', false);
            setPopulations(pageData2.items)
        }

        fetch();
    }, [])


    const initValues = {
        // brutusTestType: model?.brutusTestType ?? BrutusTestType.Kinship,
        populationId: model?.populationId,
        description: model?.description ?? '',
        references: model?.references ?? [],
        relations: model?.relations ?? [],
        h1: model?.h1 ?? { relations: [], references: [] },
        h2: model?.h2 ?? { references: [], relations: [] }
    } as InformationBrutusValuesState

    const populationOptions = model?.populations.map((p) => ({ key: p.id, value: p.id, text: p.name }))

    const testTypes = [{ text: t.brutus.kinship, value: BrutusTestType.Kinship }, { text: t.brutus.freeform, value: BrutusTestType.Freeform }, { text: `${t.brutus.undHumRem} (${t.brutus.uhr})`, value: BrutusTestType.UHR }];

    // const selectedReferences = props.model.references.filter(x => x.selected)

    return <>
        <BPContainer>
            {/* <PageHeader header={window.translation.populationView.createLocalPopulation} /> */}
            <Segment>
                <Formik initialValues={initValues}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                    onSubmit={async (values) => {
                        var mergedValues = {
                            ...values,
                            references: calcType == BrutusTestType.Kinship || calcType == BrutusTestType.UHR ? h1References : [],
                            relations: calcType == BrutusTestType.Kinship || calcType == BrutusTestType.UHR ? h1Relations : [],
                            h1: calcType == BrutusTestType.Freeform ? { ...values.h1, references: h1References, relations: h1Relations } : null,
                            h2: calcType == BrutusTestType.Freeform ? { ...values.h2, references: h2References, relations: h2Relations } : null,
                            brutusTestType: calcType
                        };
                        props.onComplete(mergedValues)
                        props.router.history.push("/parameters");
                    }}>
                    {formikProps => (
                        <Form onSubmit={formikProps.handleSubmit}>
                            <SemanticField component={Form.TextArea} name="description" label={window.translation.caseRun.runDescription} />
                            <Form.Field>
                                <label>{t.common.population}</label>
                                <Dropdown search selection options={populationOptions} value={formikProps.values.populationId} onChange={async (ev, data) => {
                                    await formikProps.setFieldValue("populationId", data.value)
                                }} />
                            </Form.Field>
                            <Form.Field>
                                <label>{window.translation.brutus.testType}</label>
                                <Dropdown
                                    selection
                                    options={testTypes}
                                    value={calcType}
                                    onChange={(ev, data) => { setCalcType(data.value as any) }}></Dropdown>
                            </Form.Field>
                            {calcType == BrutusTestType.Freeform &&
                                <Grid>
                                    <GridRow columns={2}>
                                        <GridColumn>
                                            <PedigreeEditor testType={calcType} references={h1References} relations={h1Relations}
                                                onReferenceSelected={(ref, selected) => {
                                                    setH1References(h1References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, selected: selected }
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onRelationAdded={rel => {
                                                    setH1Relations([...h1Relations, rel])
                                                }}
                                                onUntestedAdded={ref => {
                                                    setH1References([...h1References, ref])
                                                }}
                                                onRemoveRelation={rel => {
                                                    setH1Relations(h1Relations.filter(p => !(p.ancestorId == rel.ancestorId && p.descendantId == rel.descendantId && p.questioned == rel.questioned)))
                                                }}
                                                onRelationUpdate={(o, n) => {
                                                    setH1Relations(h1Relations.map(x => {
                                                        if (x.ancestorId == o.ancestorId && x.descendantId == o.descendantId)
                                                            return n
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onReferenceSexChange={(ref, isMale) => {
                                                    setH1References(h1References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, isMale: isMale }
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onMissingPersonChange={(ref, missing) => {
                                                    setH1References(h1References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, isMissingPerson: missing }
                                                        else
                                                            return x
                                                    }))
                                                }}

                                            />
                                        </GridColumn>
                                        <GridColumn>
                                            <PedigreeEditor testType={calcType} references={h2References} relations={h2Relations}
                                                onReferenceSelected={(ref, selected) => {
                                                    setH2References(h2References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, selected: selected }
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onRelationAdded={rel => {
                                                    setH2Relations([...h2Relations, rel])
                                                }}
                                                onUntestedAdded={ref => {
                                                    setH2References([...h2References, ref])
                                                }}
                                                onRemoveRelation={rel => {
                                                    setH2Relations(h2Relations.filter(p => !(p.ancestorId == rel.ancestorId && p.descendantId == rel.descendantId && p.questioned == rel.questioned)))
                                                }}
                                                onRelationUpdate={(o, n) => {
                                                    setH2Relations(h2Relations.map(x => {
                                                        if (x.ancestorId == o.ancestorId && x.descendantId == o.descendantId)
                                                            return n
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onReferenceSexChange={(ref, isMale) => {
                                                    setH2References(h2References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, isMale: isMale }
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onMissingPersonChange={(ref, missing) => {
                                                    setH2References(h2References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, isMissingPerson: missing }
                                                        else
                                                            return x
                                                    }))
                                                }}
                                            />

                                        </GridColumn>
                                    </GridRow>
                                </Grid>
                            }
                            {(calcType == BrutusTestType.Kinship || calcType == BrutusTestType.UHR) &&
                                <Grid>
                                    <GridRow>
                                        <GridColumn>
                                            <PedigreeEditor testType={calcType} references={h1References} relations={h1Relations}
                                                onReferenceSelected={(ref, selected) => {
                                                    setH1References(h1References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, selected: selected }
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onRelationAdded={rel => {
                                                    setH1Relations([...h1Relations, rel])
                                                }}
                                                onUntestedAdded={ref => {
                                                    setH1References([...h1References, ref])
                                                }}
                                                onRemoveRelation={rel => {
                                                    setH1Relations(h1Relations.filter(p => !(p.ancestorId == rel.ancestorId && p.descendantId == rel.descendantId && p.questioned == rel.questioned)))
                                                }}
                                                onRelationUpdate={(o, n) => {
                                                    setH1Relations(h1Relations.map(x => {
                                                        if (x.ancestorId == o.ancestorId && x.descendantId == o.descendantId)
                                                            return n
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onReferenceSexChange={(ref, isMale) => {
                                                    setH1References(h1References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, isMale: isMale }
                                                        else
                                                            return x
                                                    }))
                                                }}
                                                onMissingPersonChange={(ref, missing) => {
                                                    setH1References(h1References.map(x => {
                                                        if (x.id == ref.id)
                                                            return { ...x, isMissingPerson: missing }
                                                        else
                                                            return x
                                                    }))
                                                }}

                                            />
                                        </GridColumn>
                                    </GridRow>
                                </Grid>
                            }

                            <Segment basic clearing>
                                <BPButton floated="right" type="submit" positive primary>{t.caseRun.next}</BPButton>
                            </Segment>
                        </Form>
                    )}
                </Formik>
            </Segment>
        </BPContainer>

    </>;
};

interface INewUntestedProps {
    goBack: () => void;
    onOk: (values: BrutusCaseRunCreateSampleModelExt) => void;
}

export const NewUntested: React.FunctionComponent<INewUntestedProps> = (props) => {
    const t = window.translation;

    const [name, setName] = React.useState<string>()
    const [gender, setGender] = React.useState<string>()
    const [open, setOpen] = React.useState(false)

    const nanoid1 = customAlphabet('1234567890', 5)

    return <>
        <Modal as={Form}
            onClose={() => { setOpen(false); props.goBack() }}
            onOpen={() => setOpen(true)}
            open={open}
            trigger={<BPButton
                content={'Add untested person'}
                size='small'
                icon={'add'}
                compact
                type='button'
            />}>
            <Modal.Header>{'Add untested person'}</Modal.Header>
            <Modal.Content>
                <Grid>
                    <GridRow>
                        <GridColumn width={3} verticalAlign={'top'}>
                            <Form.Dropdown selection fluid label={'Gender'} defaultValue={'M'} value={gender} onChange={(e, d) => setGender(d.value.toString())} options={["Male", "Female"].map(x => { return { text: x, value: x.charAt(0) } })}></Form.Dropdown>
                        </GridColumn>
                        <GridColumn width={13} verticalAlign={'middle'}>
                            <Form.Input label={t.common.name} value={name} onChange={(ev, data) => setName(data.value)}></Form.Input>
                        </GridColumn>
                    </GridRow>
                </Grid>
            </Modal.Content>
            <Modal.Actions>
                <BPButton type='button' onClick={() => {
                    props.onOk({ id: -parseInt(nanoid1()), isMale: gender == "M", isMissingPerson: false, isUntested: true, selected: true, name: name } as BrutusCaseRunCreateSampleModelExt)
                    setOpen(false)
                }}>Add</BPButton>

                <BPButton onClick={() => setOpen(false)}>
                    {'Cancel'}
                </BPButton>
            </Modal.Actions>
        </Modal>
    </>;
};




